import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

export default function StockOpnameCountingForm() {
  const { opnameId } = useParams();
  const [opname, setOpname] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOpnameDetail();
  }, [opnameId]);

  useEffect(() => {
    // Auto print when page loads
    if (opname && !loading) {
      setTimeout(() => window.print(), 500);
    }
  }, [opname, loading]);

  const fetchOpnameDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stock-opname/${opnameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOpname(response.data.opname);
    } catch (error) {
      console.error('Error fetching opname detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !opname) {
    return (
      <div className="flex items-center justify-center min-h-screen billing-admin-shell">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p>Memuat data...</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen p-8 billing-admin-shell">
      {/* Header */}
      <div className="text-center mb-8 pb-4 border-b-4 border-black">
        <h1 className="text-3xl font-bold mb-2">FORM STOCK OPNAME</h1>
        <p className="text-xl">{opname.branch_name}</p>
        <p className="text-sm text-gray-600">Tanggal: {formatDate(opname.created_at)}</p>
      </div>

      {/* Instructions */}
      <div className="mb-6 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h3 className="font-bold mb-2">PETUNJUK PENGISIAN:</h3>
        <ol className="list-decimal list-inside text-sm space-y-1">
          <li>Hitung stok fisik secara teliti di gudang/rak</li>
          <li>Tulis jumlah hasil hitungan pada kolom "STOK FISIK"</li>
          <li>Jika ada catatan/keterangan, tulis pada kolom "KETERANGAN"</li>
          <li>Tanda tangan di bagian bawah setelah selesai</li>
          <li>Serahkan form ini ke admin untuk input ke sistem</li>
        </ol>
      </div>

      {/* Counting Table */}
      <table className="w-full border-collapse border-2 border-black mb-6">
        <thead>
          <tr className="bg-gray-200">
            <th className="border-2 border-black p-2 text-xs text-left" style={{width: '5%'}}>NO</th>
            <th className="border-2 border-black p-2 text-xs text-left" style={{width: '25%'}}>NAMA ITEM</th>
            <th className="border-2 border-black p-2 text-xs text-left" style={{width: '12%'}}>KATEGORI</th>
            <th className="border-2 border-black p-2 text-xs text-center" style={{width: '8%'}}>TIPE</th>
            <th className="border-2 border-black p-2 text-xs text-center" style={{width: '8%'}}>SATUAN</th>
            <th className="border-2 border-black p-2 text-xs text-center" style={{width: '15%'}}>STOK FISIK</th>
            <th className="border-2 border-black p-2 text-xs text-left" style={{width: '27%'}}>KETERANGAN</th>
          </tr>
        </thead>
        <tbody>
          {opname.items && opname.items.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="border-2 border-black p-2 text-xs text-center">{index + 1}</td>
              <td className="border-2 border-black p-2 text-xs font-medium">{item.name}</td>
              <td className="border-2 border-black p-2 text-xs capitalize">{item.category}</td>
              <td className="border-2 border-black p-2 text-xs text-center">
                {item.item_type === 'product' ? 'Produk' : 'Bahan'}
              </td>
              <td className="border-2 border-black p-2 text-xs text-center">{item.unit}</td>
              <td className="border-2 border-black p-2 bg-yellow-50">
                {/* Empty for manual filling */}
              </td>
              <td className="border-2 border-black p-2 bg-blue-50">
                {/* Empty for manual notes */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Signature Section */}
      <div className="grid grid-cols-3 gap-8 mt-12">
        <div className="text-center">
          <p className="mb-16 font-semibold">Dihitung Oleh:</p>
          <div className="border-t-2 border-black pt-2">
            <p className="text-sm">Nama & Tanda Tangan</p>
          </div>
        </div>

        <div className="text-center">
          <p className="mb-16 font-semibold">Diperiksa Oleh:</p>
          <div className="border-t-2 border-black pt-2">
            <p className="text-sm">Supervisor/Manager</p>
          </div>
        </div>

        <div className="text-center">
          <p className="mb-16 font-semibold">Diketahui Oleh:</p>
          <div className="border-t-2 border-black pt-2">
            <p className="text-sm">Admin/Owner</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-300 text-xs text-gray-600 text-center">
        <p>Form ini merupakan dokumen resmi stock opname</p>
        <p>Session ID: {opname.id}</p>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          @page {
            margin: 1cm;
            size: A4 landscape;
          }
          
          table {
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group;
          }
          
          /* Ensure borders are visible */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Empty cells for filling */
          .bg-yellow-50, .bg-blue-50 {
            background-color: #fffbeb !important;
            height: 40px !important;
          }
          
          .bg-gray-200 {
            background-color: #e5e7eb !important;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
          }
        }
        
        @media screen {
          /* Hide close button on print preview */
          button {
            position: fixed;
            top: 10px;
            right: 10px;
            z-index: 1000;
          }
        }
      `}</style>

      {/* Close button (screen only) */}
      <button
        onClick={() => window.close()}
        className="fixed top-4 right-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg print:hidden"
      >
        Tutup
      </button>
    </div>
  );
}
